import React from 'react';
import { Phone, MessageCircle, MapPin, Clock, Star, ArrowRight } from 'lucide-react';

export const Footer = () => {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  const services = [
    'Retour Affectif', 'Voyance & Médiumnité', 'Guérison Spirituelle',
    'Protection & Désenvoûtement', 'Chance aux Jeux', 'Réussite & Succès',
  ];

  const quickLinks = [
    { name: 'Accueil', id: 'accueil' }, { name: 'Services', id: 'services' },
    { name: 'À Propos', id: 'about' }, { name: 'Témoignages', id: 'temoignages' },
    { name: 'FAQ', id: 'faq' }, { name: 'Contact', id: 'contact' },
  ];

  const zones = [
    { title: 'Canton de Genève', areas: ['Genève-Ville', 'Carouge', 'Meyrin', 'Vernier', 'Lancy', 'Onex', 'Thonex', 'Chêne-Bougeries', 'Versoix', 'Grand-Saconnex'] },
    { title: 'Vaud', areas: ['Lausanne', 'Montreux', 'Vevey', 'Nyon', 'Morges', 'Yverdon', 'Renens', 'Pully', 'Prilly', 'Aigle'] },
    { title: 'Suisse Romande', areas: ['Fribourg', 'Neuchâtel', 'Sion', 'Sierre', 'Delémont', 'Bienne', 'La Chaux-de-Fonds', 'Monthey', 'Martigny', 'Bulle'] },
    { title: 'Suisse Alémanique', areas: ['Zürich', 'Berne', 'Bâle', 'Lucerne', 'Winterthur', 'Saint-Gall', 'Lugano', 'Thoune', 'Aarau', 'Soleure'] },
  ];

  return (
    <footer className="bg-black border-t border-amber-500/10">
      {/* Zones d'intervention */}
      <div className="py-12 bg-neutral-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <span className="text-amber-500 text-sm font-semibold tracking-widest uppercase">Zones d'Intervention</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mt-4 mb-4" style={{fontFamily: 'Cormorant Garamond, serif'}}>
              <span className="text-amber-400">Maître Diamma</span> intervient dans toute la Suisse
            </h2>
            <p className="text-amber-100/60 max-w-2xl mx-auto">
              Consultations à Genève et dans toute la Suisse. Déplacement possible. Travail à distance disponible.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {zones.map((zone) => (
              <div key={zone.title} className="bg-neutral-900/50 border border-amber-500/20 rounded-xl p-5 hover:border-amber-500/40 transition-all">
                <h4 className="text-amber-400 font-semibold text-lg mb-3 flex items-center">
                  <MapPin className="w-4 h-4 mr-2" />{zone.title}
                </h4>
                <div className="flex flex-wrap gap-1.5">
                  {zone.areas.map((area) => (
                    <span key={area} className="text-xs text-amber-200/70 bg-amber-500/5 px-2 py-1 rounded hover:bg-amber-500/15 hover:text-amber-300 transition-all">
                      {area}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="text-center">
            <p className="text-amber-200/50 text-sm">
              <strong className="text-amber-400">+50 villes</strong> couvertes en Suisse . Déplacement possible . Consultation à distance disponible
            </p>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {/* About */}
          <div>
            <h3 className="text-xl font-bold text-amber-400 mb-4" style={{fontFamily: 'Cormorant Garamond, serif'}}>Maître Diamma</h3>
            <p className="text-amber-200/60 text-sm leading-relaxed mb-4">
              Grand Marabout Africain à Genève. Spécialiste du retour affectif rapide, voyant médium sérieux. Plus de 30 ans d'expérience.
            </p>
            <div className="space-y-2">
              <a href="tel:+41767836186" className="flex items-center space-x-2 text-amber-200/70 hover:text-amber-400 transition-colors text-sm">
                <Phone className="w-4 h-4 text-amber-500" /><span>+41 76 783 61 86</span>
              </a>
              <a href="https://wa.me/41767836186" target="_blank" rel="noopener noreferrer" className="flex items-center space-x-2 text-amber-200/70 hover:text-amber-400 transition-colors text-sm">
                <MessageCircle className="w-4 h-4 text-amber-500" /><span>WhatsApp</span>
              </a>
              <div className="flex items-center space-x-2 text-amber-200/70 text-sm">
                <MapPin className="w-4 h-4 text-amber-500" /><span>Rue du Mont-Blanc 14, 1201 Genève</span>
              </div>
            </div>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-bold text-amber-400 mb-4">Nos Services</h3>
            <ul className="space-y-2">
              {services.map((s) => (
                <li key={s}>
                  <button onClick={() => scrollToSection('services')} className="text-sm text-amber-200/60 hover:text-amber-400 transition-colors flex items-center">
                    <ArrowRight className="w-3 h-3 mr-1" />{s}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Links */}
          <div>
            <h3 className="text-lg font-bold text-amber-400 mb-4">Liens Rapides</h3>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <button onClick={() => scrollToSection(link.id)} className="text-sm text-amber-200/60 hover:text-amber-400 transition-colors">
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Summary */}
          <div>
            <h3 className="text-lg font-bold text-amber-400 mb-4">Contact</h3>
            <div className="space-y-4">
              <a href="tel:+41767836186" className="block w-full text-center bg-gradient-to-r from-amber-500 to-amber-600 text-black font-bold px-4 py-3 rounded-lg hover:from-amber-400 hover:to-amber-500 transition-all text-sm" data-testid="footer-call-btn">
                <Phone className="w-4 h-4 inline mr-1" />Appeler Maintenant
              </a>
              <a href="https://wa.me/41767836186" target="_blank" rel="noopener noreferrer" className="block w-full text-center bg-green-600 hover:bg-green-500 text-white font-bold px-4 py-3 rounded-lg transition-all text-sm" data-testid="footer-whatsapp-btn">
                <MessageCircle className="w-4 h-4 inline mr-1" />WhatsApp
              </a>
              <div className="text-center">
                <div className="flex items-center justify-center space-x-1 mb-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-amber-400 fill-amber-400" />
                  ))}
                </div>
                <p className="text-amber-200/50 text-xs">4.9/5 - 800+ avis</p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom SEO */}
        <div className="border-t border-amber-500/10 pt-8 mb-6">
          <div className="p-4 bg-neutral-900/30 rounded-lg">
            <p className="text-amber-200/40 text-xs text-center leading-relaxed">
              <strong className="text-amber-200/60">Maître Diamma</strong> - Grand Marabout Africain à Genève - Spécialiste du retour affectif rapide - Voyant médium sérieux - Expert en désenvoûtement et protection spirituelle - 30 ans d'expérience - 800+ clients satisfaits - Résultats garantis en 3 à 7 jours - Consultation gratuite - Discrétion absolue - +41 76 783 61 86
            </p>
          </div>
        </div>

        {/* Copyright */}
        <div className="text-center space-y-2">
          <p className="text-amber-200/40 text-xs">
            Disponible 7j/7 de 8h à 23h30 | Consultation Gratuite | Facilité de paiement
          </p>
          <p className="text-amber-200/30 text-xs">
            &copy; {new Date().getFullYear()} Maître Diamma - Marabout Genève. Tous droits réservés. | Discrétion absolue garantie.
          </p>
        </div>
      </div>
    </footer>
  );
};
